/** Sample rate the voice service's speech-to-text expects (`pcm_s16le`, mono). */
export const STT_SAMPLE_RATE = 16_000;

/**
 * Streaming resampler from the microphone rate (usually 44.1 or 48 kHz) to 16 kHz mono.
 * Downsampling averages the input samples that fall into each output sample (a cheap low-pass
 * that avoids most aliasing); upsampling interpolates linearly. State carries across chunks so
 * frame boundaries never click or drop samples.
 */
export class Resampler {
  private readonly ratio: number;
  private pending = new Float32Array(0);
  /** Fractional read position inside `pending`. */
  private pos = 0;

  constructor(
    readonly inputRate: number,
    readonly outputRate: number = STT_SAMPLE_RATE,
  ) {
    if (!(inputRate > 0) || !(outputRate > 0)) throw new Error("sample rates must be positive");
    this.ratio = inputRate / outputRate;
  }

  push(input: Float32Array): Float32Array {
    const buf = new Float32Array(this.pending.length + input.length);
    buf.set(this.pending, 0);
    buf.set(input, this.pending.length);
    const out: number[] = [];
    if (this.ratio >= 1) {
      while (this.pos + this.ratio <= buf.length) {
        const start = this.pos;
        const end = this.pos + this.ratio;
        let sum = 0;
        let weight = 0;
        for (let i = Math.floor(start); i < Math.ceil(end); i++) {
          const w = Math.min(i + 1, end) - Math.max(i, start);
          sum += buf[i]! * w;
          weight += w;
        }
        out.push(weight > 0 ? sum / weight : 0);
        this.pos = end;
      }
    } else {
      while (this.pos + 1 < buf.length) {
        const i = Math.floor(this.pos);
        const f = this.pos - i;
        out.push(buf[i]! * (1 - f) + buf[i + 1]! * f);
        this.pos += this.ratio;
      }
    }
    const consumed = Math.min(Math.floor(this.pos), buf.length);
    this.pending = buf.slice(consumed);
    this.pos -= consumed;
    return Float32Array.from(out);
  }
}

/** Float samples in [-1, 1] to signed 16-bit PCM (clipped). */
export function floatToInt16(input: Float32Array): Int16Array {
  const out = new Int16Array(input.length);
  for (let i = 0; i < input.length; i++) {
    const s = Math.max(-1, Math.min(1, input[i]!));
    out[i] = s < 0 ? Math.round(s * 0x8000) : Math.round(s * 0x7fff);
  }
  return out;
}

/** Collects samples and emits fixed-size frames (e.g. 480 samples = 30 ms at 16 kHz). */
export class FrameBuffer {
  private buf: Int16Array;
  private used = 0;

  constructor(readonly frameSamples: number) {
    if (frameSamples <= 0) throw new Error("frame size must be positive");
    this.buf = new Int16Array(frameSamples);
  }

  push(samples: Int16Array, emit: (frame: Int16Array) => void): void {
    let offset = 0;
    while (offset < samples.length) {
      const n = Math.min(this.frameSamples - this.used, samples.length - offset);
      this.buf.set(samples.subarray(offset, offset + n), this.used);
      this.used += n;
      offset += n;
      if (this.used === this.frameSamples) {
        emit(this.buf);
        this.buf = new Int16Array(this.frameSamples);
        this.used = 0;
      }
    }
  }

  /** Emits what is left (a short last frame), if anything. */
  flush(emit: (frame: Int16Array) => void): void {
    if (this.used > 0) emit(this.buf.slice(0, this.used));
    this.buf = new Int16Array(this.frameSamples);
    this.used = 0;
  }
}

/**
 * Decodes a byte stream of `pcm_s16le` into float samples. Network chunks may split a sample
 * between two chunks; the odd byte is carried to the next chunk.
 */
export class PcmDecoder {
  private carry: number | null = null;

  push(bytes: Uint8Array): Float32Array {
    const extra = this.carry === null ? 0 : 1;
    const total = bytes.length + extra;
    const samples = Math.floor(total / 2);
    const out = new Float32Array(samples);
    const byteAt = (i: number): number =>
      extra === 1 ? (i === 0 ? this.carry! : bytes[i - 1]!) : bytes[i]!;
    for (let s = 0; s < samples; s++) {
      const lo = byteAt(s * 2);
      const hi = byteAt(s * 2 + 1);
      let v = (hi << 8) | lo;
      if (v >= 0x8000) v -= 0x10000;
      out[s] = v / 0x8000;
    }
    this.carry = total % 2 === 1 ? byteAt(total - 1) : null;
    return out;
  }

  /** True when half a sample is still waiting for its second byte. */
  get hasCarry(): boolean {
    return this.carry !== null;
  }
}
